<template>
  <template v-if="['extra-small'].includes(size)">
    <div
      :data-tint="statusTint"
      class="flex rounded-md shadow-sm dark:border dark:bg-slate-700"
    >
      <Popover>
        <Float
          portal
          :auto-placement="{
            allowedPlacements: [
              'bottom-start',
              'top-start',
              'bottom-end',
              'top-end'
            ]
          }"
          :offset="4"
        >
          <PopoverButton
            class="to-bg-primary-600 flex shrink-0 items-center justify-center gap-1 rounded-md bg-primary-500 bg-linear-to-br px-1 py-0.5 text-xs font-medium text-contrast-primary"
          >
            <FontAwesomeIcon :icon="['fal', task_status_option.icon]" />
            <span>{{ translatedText(task?.name) }}</span>
          </PopoverButton>
          <PopoverPanel
            v-slot="{ close }"
            class="bg-surface-raised z-50 flex w-48 flex-col gap-1 rounded-lg p-2 shadow-2xl"
          >
            <button
              v-for="status in task_statuses"
              :key="status.id"
              :data-tint="`task-status-${status.id}`"
              class="task-gradient relative rounded-md px-2 py-1 pl-6 text-left"
              @click="() => updatedStatus(status.id, close)"
            >
              <span
                v-if="displayStatus === parseInt(status.id)"
                class="absolute inset-y-0 left-2 flex items-center"
              >
                <span class="h-2 w-2 rounded-full bg-white">&nbsp;</span>
              </span>
              <span>{{ status.name }}</span>
            </button>
          </PopoverPanel>
        </Float>
      </Popover>
    </div>
  </template>
  <template v-else-if="['mini', 'small'].includes(size)">
    <div
      :data-tint="statusTint"
      class="group flex rounded-md shadow-sm dark:bg-slate-700"
    >
      <Popover>
        <Float
          portal
          :auto-placement="{
            allowedPlacements: [
              'bottom-start',
              'top-start',
              'bottom-end',
              'top-end'
            ]
          }"
          :offset="4"
        >
          <PopoverButton
            class="to-bg-primary-600 flex h-10 w-11 shrink-0 items-center justify-center rounded-l-md bg-primary-500 bg-linear-to-br text-lg font-medium text-contrast-primary"
          >
            <FontAwesomeIcon :icon="['fal', task_status_option.icon]" />
          </PopoverButton>
          <PopoverPanel
            v-slot="{ close }"
            class="bg-surface-raised z-50 flex w-48 flex-col gap-1 rounded-lg p-2 shadow-2xl"
          >
            <button
              v-for="status in task_statuses"
              :key="status.id"
              :data-tint="`task-status-${status.id}`"
              class="task-gradient relative rounded-md px-2 py-1 pl-6 text-left"
              @click="() => updatedStatus(status.id, close)"
            >
              <span
                v-if="displayStatus === parseInt(status.id)"
                class="absolute inset-y-0 left-2 flex items-center"
              >
                <span class="h-2 w-2 rounded-full bg-white">&nbsp;</span>
              </span>
              <span>{{ status.name }}</span>
            </button>
          </PopoverPanel>
        </Float>
      </Popover>
      <div
        class="flex flex-1 items-center justify-between gap-2 truncate rounded-r-md border-t border-r border-b bg-white px-2 dark:bg-slate-700"
      >
        <div class="flex-1 truncate text-sm">
          <div class="flex-1 truncate text-sm">
            <div class="flex flex-row items-center">
              <h4 class="flex-1 font-medium text-gray-900 dark:text-white">
                {{ translatedText(task?.name) }}
              </h4>
              <FontAwesomeIcon
                v-if="task?.activity_log_id && !hideActivityBadge"
                :icon="['fal', 'font-awesome']"
                class="text-gray-400"
              />
            </div>
            <p
              v-if="task.due_date_time"
              class="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400"
            >
              <span
                v-if="task.due_date_type === 'before_date'"
                class="font-medium"
              >
                →
              </span>
              <span
                v-else-if="task.due_date_type === 'on_date'"
                class="h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400 dark:bg-gray-500"
              />
              {{ formattedDueDateTime }}
            </p>
          </div>
        </div>
        <div class="flex shrink-0 items-center gap-1">
          <button v-if="isScreenDisplay" @click="lightModalOpened = true">
            <FontAwesomeIcon :icon="['fal', 'ellipsis-vertical']" />
          </button>
          <button
            v-else-if="$can('manage_dossier') && !notEditable"
            class="flex h-7 w-7 items-center justify-center rounded transition-colors duration-500 group-hover:bg-slate-100"
            @click="openEditDialogTaskDossier"
          >
            <FontAwesomeIcon :icon="['fal', 'ellipsis-vertical']" />
          </button>
          <button
            v-else-if="$can('super-admin') || $can('screen')"
            @click="lightModalOpened = true"
          >
            <FontAwesomeIcon :icon="['fal', 'ellipsis-vertical']" />
          </button>
        </div>
      </div>
    </div>
  </template>
  <template v-else-if="size === 'normal'">
    <div
      class="group relative flex flex-col gap-2 overflow-visible squircle-lg rounded-lg border p-3"
      :draggable="cardDraggable"
      @dragstart="handleDragStart"
      @dragend="handleDragEnd"
    >
      <div class="flex shrink-0 flex-row items-center gap-1">
        <input
          v-if="selectable"
          type="checkbox"
          data-test="task-select"
          class="h-4 w-4 shrink-0 cursor-pointer accent-primary"
          :class="{ 'hidden sm:block': hideSelectOnNarrow }"
          :checked="selected"
          :aria-label="$t('button.select')"
          draggable="false"
          @click.stop
          @dragstart.stop.prevent
          @change="$emit('toggle-select')"
        />
        <div
          v-if="draggable || showDragHandle"
          data-drag-handle
          class="cursor-move overflow-hidden transition-all"
          @mouseenter="isCardDraggable = true"
          @mouseleave="isCardDraggable = false"
        >
          <FontAwesomeIcon :icon="['fas', 'arrows-up-down-left-right']" />
        </div>
        <div class="flex flex-1 flex-col overflow-hidden">
          <h3
            class="truncate text-base leading-tight font-bold capitalize-first dark:text-white"
            :class="
              $can('manage_dossier') ? 'cursor-pointer' : 'cursor-default'
            "
            @click="$can('manage_dossier') ? openEditDialogTaskDossier() : ''"
          >
            {{ translatedText(task?.name) }}
          </h3>
        </div>
        <div
          v-if="task.activity_log_id && !hideActivityBadge"
          class="shrink-0"
          :title="$t('global.consignments_title')"
        >
          <FontAwesomeIcon
            :icon="['fal', 'font-awesome']"
            class="text-xs text-gray-400"
          />
        </div>
        <div class="shrink-0">
          <BaseDropDownMenu
            v-if="$can('manage_dossier')"
            :options="dotMenuOptions"
            size="extra-small"
            :is-button="false"
          />
        </div>
      </div>
      <!-- On the task dashboard (displayFields set), the configurable item line replaces this subtitle. -->
      <h3
        v-if="!displayFields && task.parent_type === 'App\\Models\\ItemDossier'"
        class="-mt-1 truncate text-xs leading-tight font-light text-gray-600 capitalize-first dark:text-white"
      >
        {{ translatedText(task.parent.name) }}
      </h3>
      <h3
        v-else-if="!displayFields && task.parent_type === 'App\\Models\\Event'"
        class="-mt-1 truncate text-xs leading-tight font-light text-gray-600 capitalize-first dark:text-white"
      >
        {{ task.parent.type ? $t('enums.event.type.' + task.parent.type) : '' }}
        <template v-if="task.parent.date_from">
          · {{ $h.formatDate(task.parent.date_from, 'll') }}
        </template>
      </h3>
      <div class="flex-1">
        <p class="line-clamp-2 text-sm text-gray-600 dark:text-gray-300">
          {{ task?.remarks[$i18n.locale] }}
        </p>
        <div>
          <span v-if="task?.deceased?.full_name" class="text-xs text-gray-500">
            {{ task.deceased.full_name }}
          </span>
          <template v-if="task.dossier">
            <RouterLink
              :to="{
                name: 'dossier_form_edit_route',
                params: { id: task.dossier.id }
              }"
              class="flex flex-col text-xs text-primary dark:text-white"
            >
              <span>{{ task.dossier.concernedPerson?.full_name }}</span>
              <span>{{ task.dossier.reference }}</span>
            </RouterLink>
          </template>
          <template v-if="displayFields">
            <span
              v-if="
                displayFields.item && (task.item?.name || task.parent_label)
              "
              class="block text-xs text-gray-600 dark:text-gray-300"
            >
              {{ task.item?.name ?? task.parent_label }}
            </span>
            <span
              v-if="
                displayFields.funeral_home && task.dossier?.funeral_home_name
              "
              class="block text-xs text-gray-500"
            >
              {{ task.dossier.funeral_home_name }}
            </span>
            <span
              v-if="displayFields.company && task.dossier?.company_name"
              class="block text-xs text-gray-500"
            >
              {{ task.dossier.company_name }}
            </span>
            <span
              v-if="displayFields.funeral_date && task.funeral_date"
              data-test="task-funeral-date"
              :title="$t('attributes.burial_date')"
              class="flex items-center gap-1 text-xs text-gray-500"
            >
              <FontAwesomeIcon :icon="['fal', 'church']" />
              {{ formattedFuneralDate }}
            </span>
            <span
              v-if="displayFields.created_at && task.created_at"
              data-test="task-created-at"
              :title="$t('attributes.creation_date')"
              class="flex items-center gap-1 text-xs text-gray-500"
            >
              <FontAwesomeIcon :icon="['fal', 'calendar']" />
              {{ formattedCreatedAt }}
            </span>
          </template>
        </div>
      </div>
      <div class="flex flex-row items-center justify-between gap-2">
        <div class="flex flex-row items-center gap-2">
          <button
            v-if="statusClickCycle && $can('manage_dossier')"
            :data-tint="`task-status-${displayStatus}`"
            class="task-gradient flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium"
            @click.stop="handleCycleStatus"
          >
            <FontAwesomeIcon :icon="['fal', task_status_option.icon]" />
            <span>{{ $t('enums.task.status.' + displayStatus) }}</span>
          </button>
          <SelectMiniStatusTask
            v-else-if="!statusClickCycle"
            :model-value="displayStatus"
            :disabled="!$can('manage_dossier') || cardDraggable"
            @value-changed="updatedStatus"
          />
          <button
            v-if="$can('manage_dossier')"
            class="flex h-6 w-6 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-600"
            @click.stop="activityLogModalOpened = true"
          >
            <FontAwesomeIcon :icon="['fal', 'clock']" class="text-xs" />
          </button>
          <span
            v-if="
              (!displayFields || displayFields.due_date) && task.due_date_time
            "
            data-test="task-due-date"
            class="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400"
          >
            <span
              v-if="task.due_date_type === 'before_date'"
              class="font-medium"
            >
              →
            </span>
            <span
              v-else-if="task.due_date_type === 'on_date'"
              class="h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400 dark:bg-gray-500"
            />
            {{ formattedDueDateTime }}
          </span>
        </div>
        <div class="flex shrink-0 flex-row gap-1">
          <div
            v-if="
              (!displayFields || displayFields.responsable) &&
              task.users?.manager
            "
            class="shrink-0"
          >
            <UserAvatarPopover
              :user-id="task.users.manager.id"
              size="extra-small"
            />
          </div>
          <div
            v-if="!displayFields || displayFields.assigned"
            class="flex flex-row-reverse empty:hidden"
          >
            <span v-for="executor in task.users?.executors" :key="executor.id">
              <UserAvatarPopover :user-id="executor.id" size="extra-small" />
            </span>
          </div>
        </div>
      </div>
    </div>
  </template>
  <BaseCenteredModal
    v-model:is-modal-open="lightModalOpened"
    size="medium"
    :title="localTask.name[$i18n.locale]"
  >
    <template #body>
      <div class="min-h-80 w-full">
        <div class="grid-form w-full">
          <div>
            <SelectComponent
              v-model="localTask.status"
              name=""
              :values="task_statuses"
            />
          </div>
          <div class="col-start-1">
            <BaseShowEditRadio
              v-model="localTask.due_date_type"
              name="due_date_type"
              :label="$t('attributes.due_date_type')"
              :options="due_date_types"
              edition-mode
              :required="true"
              :translatable="false"
              :is-vertical="false"
            />
          </div>
          <div>
            <BaseShowEditDateTimePickerv2
              v-model:date="localTask.due_date_time"
              v-model:time-undefined="localTask.due_date_undefined_time"
              name="due_date_time"
              :label-date="$t('attributes.due_date')"
              :label-time="$t('attributes.hour')"
              edition-mode
            />
          </div>
          <div class="col-span-2">
            <BaseShowEditTextArea
              v-model="localTask.remarks[$i18n.locale]"
              :label="$t('attributes.remarks')"
              name="remarks"
              edition-mode
            />
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-end">
        <BaseButton :title="$t('button.send')" @click="quickUpdate" />
      </div>
    </template>
  </BaseCenteredModal>
  <TaskDossierModal
    v-model:open="modalOpened"
    v-model:edition-mode="modalEditionMode"
    :task-id="selectedTaskId"
    :is-task-dossier="isItemTask"
    :refresh-task-dossier="refreshTaskDossier"
    @refresh-list="$emit('refresh-list')"
    @tasks-updated="$emit('tasks-updated', $event)"
  />
  <ActivityLogModal
    v-model:open="activityLogModalOpened"
    :model-id="task.id"
    model-type="task-dossier"
  />
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { Languages, TaskStatus } from '@/assets/enums'
import SelectMiniStatusTask from '@/views/dossier/form/subpages/taskDossier/components/taskDossierList/components/SelectMiniStatusTask'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseDropDownMenu from '@c/addf-package/components/dropdown/BaseDropDownMenu.vue'
import UserAvatarPopover from '@c/Avatar/Popover/UserAvatarPopover.vue'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { Float } from '@headlessui-float/vue'
import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import BaseShowEditTextArea from '@c/addf-package/components/BaseShowEditInput/BaseShowEditTextArea.vue'
import BaseShowEditRadio from '@c/addf-package/components/BaseShowEditRadioGroup/BaseShowEditRadio.vue'
import BaseShowEditDateTimePickerv2 from '@c/addf-package/components/BaseShowEditDatePicker/BaseShowEditDateTimePickerv2.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import TaskDossierModal from '@c/baseComponents/modal/taskDossier/TaskDossierModal.vue'
import ActivityLogModal from '@ui/modal/activityLog/ActivityLogModal.vue'
import { TASK_STATUS_CYCLE } from './taskStatusCycle'
import { taskDisplayName } from './taskDisplayName'
import { taskDossierMenuOptions } from './taskDossierMenuOptions'
import { formatTaskDate } from '@/components/Dashboard/Content/Task/utils/taskDates'

export default {
  name: 'TaskDossierCard',
  components: {
    ActivityLogModal,
    BaseButton,
    BaseShowEditDateTimePickerv2,
    BaseShowEditRadio,
    BaseShowEditTextArea,
    SelectComponent,
    BaseCenteredModal,
    Popover,
    PopoverButton,
    PopoverPanel,
    Float,
    UserAvatarPopover,
    BaseDropDownMenu,
    FontAwesomeIcon,
    SelectMiniStatusTask,
    TaskDossierModal
  },
  props: {
    task: {
      type: Object,
      required: true
    },
    isItemTask: {
      type: Boolean,
      required: false,
      default: false
    },
    refreshTaskDossier: {
      type: Boolean,
      required: false,
      default: false
    },
    size: {
      type: String,
      required: false,
      default: 'normal',
      validator: (value) => ['extra-small', 'mini', 'normal'].includes(value)
    },
    draggable: {
      type: Boolean,
      required: false,
      default: false
    },
    /** Shows the move icon for a parent that drives the drag itself (SortableJS handle). */
    showDragHandle: {
      type: Boolean,
      required: false,
      default: false
    },
    notEditable: {
      type: Boolean,
      required: false,
      default: false
    },
    hideActivityBadge: {
      type: Boolean,
      required: false,
      default: false
    },
    statusClickCycle: {
      type: Boolean,
      required: false,
      default: false
    },
    isScreenDisplay: {
      type: Boolean,
      required: false,
      default: false
    },
    /** Task dashboard bulk actions: shows a checkbox left of the title. */
    selectable: {
      type: Boolean,
      required: false,
      default: false
    },
    selected: {
      type: Boolean,
      required: false,
      default: false
    },
    /** Cards view: the checkbox gives way to the title on narrow screens. */
    hideSelectOnNarrow: {
      type: Boolean,
      required: false,
      default: false
    },
    displayFields: {
      type: Object,
      required: false,
      default: null
    }
  },
  emits: [
    'update:task',
    'is-dragging',
    'refresh-list',
    'task-status-updated',
    'tasks-updated',
    'task-deleted',
    'toggle-select'
  ],
  data() {
    return {
      lightModalOpened: false,
      modalOpened: false,
      activityLogModalOpened: false,
      selectedTaskId: null,
      Languages: Languages,
      modalEditionMode: false,
      websocket_channel: null,
      isCardDraggable: false,
      pendingStatus: null
    }
  },
  computed: {
    ...mapGetters({
      dossier: 'dossier/getDossier',
      metadata: 'dossier/getDossierMetadata',
      task_statuses: 'metadata/getMetadataTaskStatuses',
      due_date_types: 'metadata/getDueDateTypesTaskDossier',
      user: 'auth/getUser'
    }),
    cardDraggable() {
      return this.draggable && this.isCardDraggable
    },
    localTask: {
      get() {
        return this.task
      },
      set(value) {
        this.$emit('update:task', value)
      }
    },
    dotMenuOptions() {
      return taskDossierMenuOptions((key) => this.$t(key), {
        view: () => this.openViewDialogTaskDossier(),
        edit: () => this.openEditDialogTaskDossier(),
        activityLog: () => (this.activityLogModalOpened = true),
        delete: () => this.handleDeleteTaskDossier()
      })
    },
    displayStatus() {
      return this.pendingStatus ?? this.task.status
    },
    task_status_option() {
      return (
        this.task_statuses?.find(
          (task_status) => task_status.id === this.displayStatus
        ) ?? {}
      )
    },
    nextStatus() {
      const idx = TASK_STATUS_CYCLE.indexOf(this.displayStatus)
      return TASK_STATUS_CYCLE[(idx + 1) % TASK_STATUS_CYCLE.length]
    },
    c_status_change_activities_ordered_desc() {
      return []
      // const statusChangeActivities = this.task.status_change_activities
      // return statusChangeActivities
      //   .sort((activity1, activity2) => activity2.id - activity1.id)
      //   .slice(0, 2)
    },
    allTasks() {
      return this.$route.name === 'tasks_index_route'
    },
    formattedFuneralDate() {
      return (
        formatTaskDate(
          this.task.funeral_date,
          this.task.funeral_date_undefined_time
        ) || null
      )
    },
    formattedCreatedAt() {
      return formatTaskDate(this.task.created_at, false) || null
    },
    formattedDueDateTime() {
      return (
        formatTaskDate(
          this.task.due_date_time,
          this.task.due_date_undefined_time
        ) || null
      )
    },
    statusTint() {
      switch (this.displayStatus) {
        case TaskStatus.TO_DO:
          return 'paradise-pink'
        case TaskStatus.RUNNING:
          return 'warning'
        case TaskStatus.DONE:
          return 'pistachio'
        case TaskStatus.AWAITING:
          return 'sea-serpent'
        default:
          return 'independence'
      }
    }
  },
  watch: {
    modalOpened(newValue) {
      if (newValue === false) {
        this.$emit('refresh-list')
      }
    },
    'task.status'(newVal) {
      if (this.pendingStatus !== null && newVal === this.pendingStatus) {
        this.pendingStatus = null
      }
    }
  },
  mounted() {
    if (this.$pusher) {
      this.websocket_channel = this.$pusher
        .subscribe(`task_dossier.${this.task.id}`)
        .bind('event', (data) => {
          if (this.user.id !== data.triggered_by.id) {
            if (data.event === 'task_dossier.updated') {
              this.processFetchTaskDossierByIdAction({
                id: this.task.id
              }).then((response) => {
                this.localTask = response.task_dossier
              })
            }
          }
        })
    }
  },
  unmounted() {
    this.websocket_channel?.unsubscribe()
  },
  methods: {
    translatedText(translations) {
      return taskDisplayName(translations, this.$h)
    },
    ...mapActions({
      updateDossierTaskStatus: 'dossier/updateDossierTaskStatus',
      processDeleteTaskDossierByIdAction: 'dossier/deleteTaskDossierById',
      processUndoDeleteTaskDossierByIdAction:
        'dossier/undoDeleteTaskDossierById',
      processFetchDossierByIDAction: 'dossier/fetchDossierByID',
      processFetchTaskDossierByIdAction: 'taskDossier/fetchTaskDossierById',
      processSetDataTransferObjectAction: 'taskDossier/setDataTransferObject',
      processFetchDossierStatusSummaryAction:
        'dossier/fetchDossierStatusSummary',
      processUpdateDossierTaskAction: 'dossier/updateDossierTask'
    }),
    handleCycleStatus() {
      if (this.task.form_on_start || this.task.form_on_end) {
        this.updatedStatus(this.nextStatus)
        return
      }
      const next = this.nextStatus
      this.pendingStatus = next
      const successMsg = this.$t('toasts.updated_f', {
        var: this.$t('attributes.task')
      })
      this.updateDossierTaskStatus({
        id: this.task.id,
        new_status: next,
        isItemTask: this.isItemTask
      })
        .then((response) => {
          this.$h.toastSuccess(successMsg)
          this.$emit('task-status-updated', {
            taskId: this.task.id,
            newStatus: next,
            task: response?.task_dossier ?? null
          })
        })
        .catch(() => {
          this.pendingStatus = null
        })
    },
    handleDragStart(event) {
      setTimeout(() => {
        event.target.classList.add('!opacity-0')
      }, 1)
      this.$emit('is-dragging', true)
      this.processSetDataTransferObjectAction({
        task: this.task
      })
    },
    handleDragEnd(event) {
      this.$emit('is-dragging', false)
      event.target.classList.remove('!opacity-0')
      this.processSetDataTransferObjectAction({})
    },
    updatedStatus(newValue, close) {
      if (newValue !== this.displayStatus) {
        if (!this.task.form_on_start && !this.task.form_on_end) {
          this.pendingStatus = newValue
          if (typeof close === 'function') {
            close()
          }
          let success_message = this.$t('toasts.updated_f', {
            var: this.$t('attributes.task')
          })
          this.updateDossierTaskStatus({
            id: this.task.id,
            new_status: newValue,
            isItemTask: this.isItemTask
          })
            .then(() => {
              this.$h.toastSuccess(success_message)
              if (this.dossier) {
                this.processFetchDossierStatusSummaryAction({
                  dossier_id: this.dossier.id
                })
              }
              this.$emit('refresh-list')
            })
            .catch(() => {
              this.pendingStatus = null
            })
        }
      }
    },
    handleSwitchEditionMode(editionMode) {
      this.editionMode = editionMode
    },
    openEditDialogTaskDossier() {
      this.selectedTaskId = this.task.id
      this.modalOpened = true
      this.modalEditionMode = false
    },
    openViewDialogTaskDossier() {
      this.selectedTaskId = this.task.id
      this.modalOpened = true
      this.modalEditionMode = false
    },
    async handleDeleteTaskDossier() {
      await this.processDeleteTaskDossierByIdAction({
        task_id: this.task.id
      }).then((response) => {
        // On the task dashboard (displayFields set), the refresh unmounts this card while the
        // skeleton shows: the container owns the refresh and the undo toast instead.
        if (this.displayFields) {
          this.$emit('task-deleted', { id: response.id })
          return
        }
        this.$emit('refresh-list')
        this.emitTasksUpdated(response.tasks)
        if (this.dossier && this.$route.name !== 'tasks_index_route') {
          this.processFetchDossierStatusSummaryAction({
            dossier_id: this.dossier.id
          })
        }
        if (this.allTasks) {
          this.$emit('task-deleted', { id: response.id })
        } else {
          this.$h.toastAction(
            'delete',
            this.$t('toasts.deleted_f', { var: this.$t('attributes.task') }),
            null,
            [
              {
                title: this.$t('button.cancel'),
                class: 'text-red-700',
                action: () => {
                  this.undoDeleteTaskDossier(response.id)
                }
              }
            ]
          )
        }
      })
    },
    async undoDeleteTaskDossier(id) {
      await this.processUndoDeleteTaskDossierByIdAction({
        task_id: id
      }).then((response) => {
        this.$emit('refresh-list')
        this.emitTasksUpdated(response.tasks)
        if (this.dossier && this.$route.name !== 'tasks_index_route') {
          this.processFetchDossierStatusSummaryAction({
            dossier_id: this.dossier.id
          })
        }
      })
    },
    emitTasksUpdated(tasks) {
      if (!tasks) return
      const parentType = this.task.parent_type
      const parentId = this.task.parent_id
      if (parentType && parentId) {
        this.$emit('tasks-updated', {
          parent_type: parentType,
          parent_id: parentId,
          tasks
        })
      }
    },
    quickUpdate() {
      this.processUpdateDossierTaskAction({
        id: this.task.id,
        task: this.task
      }).then((response) => {
        this.$emit('refresh-list')
        this.lightModalOpened = false
      })
    }
  }
}
</script>
